import { useReducer, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/authContext';
import { submitStudentIDCard, getStudentIDSubmissionStatus, getStudentIDSubmissionsHistory } from '../services/userService';
import axiosInstance from '../libs/axiosInstance';
import { toast } from 'sonner';

const initialState = {
  isUploading: false,
  isPolling: false,
  localStatus: null,
  localError: null,
  history: [],
  loadingHistory: false,
};

function verificationReducer(state, action) {
  switch (action.type) {
    case 'START_UPLOAD':
      return {
        ...state,
        isUploading: true,
        localError: null,
      };
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        isUploading: false,
      };
    case 'UPLOAD_FAILURE':
      return {
        ...state,
        isUploading: false,
        localError: action.payload,
      };
    case 'START_POLLING':
      return {
        ...state,
        isPolling: true,
        localStatus: 'pending',
        localError: null,
      };
    case 'POLL_RESULT':
      return {
        ...state,
        isPolling: false,
        localStatus: action.payload.status,
        localError: action.payload.error || null,
      };
    case 'FETCH_HISTORY_START':
      return {
        ...state,
        loadingHistory: true,
      };
    case 'FETCH_HISTORY_SUCCESS':
      return {
        ...state,
        loadingHistory: false,
        history: action.payload,
      };
    case 'FETCH_HISTORY_FAILURE':
      return {
        ...state,
        loadingHistory: false,
      };
    default:
      return state;
  }
}

export default function useStudentIDVerification() {
  const { profile, setProfile, setUser } = useAuth();
  const [state, dispatch] = useReducer(verificationReducer, initialState);
  
  const { isUploading, isPolling, localStatus, localError, history, loadingHistory } = state;
  
  const pollingTimerRef = useRef(null);

  // Derive status, error, and image URL from state/profile
  const status = localStatus || profile?.id_verification_status || 'unuploaded';
  const error = localError !== null ? localError : (profile?.id_verification_error || null);
  const studentIdCard = profile?.student_id_card || null;

  // Clear polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/account/profile/');
      if (response.data?.user) {
        setUser(response.data.user);
        setProfile(response.data.profile);
        return response.data.profile;
      }
    } catch (err) {
      console.error('Failed to refresh profile:', err);
    }
    return null;
  }, [setProfile, setUser]);

  const pollSubmission = useCallback((submissionId) => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }

    dispatch({ type: 'START_POLLING' });

    pollingTimerRef.current = setInterval(async () => {
      try {
        const data = await getStudentIDSubmissionStatus(submissionId);
        
        if (data.status === 'verified') {
          clearInterval(pollingTimerRef.current);
          pollingTimerRef.current = null;
          dispatch({ type: 'POLL_RESULT', payload: { status: 'verified' } });
          toast.success('Identity verified successfully!');
          await refreshProfile();
        } else if (data.status === 'failed') {
          clearInterval(pollingTimerRef.current);
          pollingTimerRef.current = null;
          dispatch({ type: 'POLL_RESULT', payload: { status: 'failed', error: data.error_message || 'Verification failed' } });
          toast.error(data.error_message || 'Verification failed');
          await refreshProfile();
        }
      } catch (err) {
        console.error('Error polling submission status:', err);
      }
    }, 7000); // Poll every 7 seconds
  }, [refreshProfile]);

  const uploadIDCard = useCallback(async (file) => {
    dispatch({ type: 'START_UPLOAD' });
    
    try {
      const data = await submitStudentIDCard(file);
      dispatch({ type: 'UPLOAD_SUCCESS' });
      
      if (data.id) {
        pollSubmission(data.id);
      } else {
        await refreshProfile();
      }
      return data;
    } catch (err) {
      console.error('Error in uploadIDCard:', err);
      let errorMsg = 'Failed to upload student ID card. Please check the image and try again.';
      if (err?.response?.data?.id_card_image) {
        const fieldError = err.response.data.id_card_image;
        errorMsg = Array.isArray(fieldError) ? fieldError[0] : fieldError;
      } else if (err?.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      dispatch({ type: 'UPLOAD_FAILURE', payload: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  }, [pollSubmission, refreshProfile]);

  const fetchHistory = useCallback(async () => {
    dispatch({ type: 'FETCH_HISTORY_START' });
    try {
      const data = await getStudentIDSubmissionsHistory();
      dispatch({ type: 'FETCH_HISTORY_SUCCESS', payload: data });
      return data;
    } catch (err) {
      console.error('Error fetching submissions history:', err);
      dispatch({ type: 'FETCH_HISTORY_FAILURE' });
    }
    return [];
  }, []);

  // Auto-resume polling if page is reloaded and user has a pending verification
  useEffect(() => {
    if (profile?.id_verification_status === 'pending' && !isPolling && !pollingTimerRef.current) {
      const startPollingPending = async () => {
        try {
          const subs = await getStudentIDSubmissionsHistory();
          const pendingSub = subs.find(s => s.status === 'pending');
          if (pendingSub) {
            pollSubmission(pendingSub.id);
          } else {
            // Fallback: poll profile endpoint directly if history can't be fetched
            dispatch({ type: 'START_POLLING' });
            pollingTimerRef.current = setInterval(async () => {
              const updatedProfile = await refreshProfile();
              if (updatedProfile && updatedProfile.id_verification_status !== 'pending') {
                clearInterval(pollingTimerRef.current);
                pollingTimerRef.current = null;
                dispatch({
                  type: 'POLL_RESULT',
                  payload: {
                    status: updatedProfile.id_verification_status,
                    error: updatedProfile.id_verification_error,
                  }
                });
              }
            }, 3000);
          }
        } catch (err) {
          console.error('Failed to auto-resume pending polling:', err);
        }
      };
      startPollingPending();
    }
  }, [profile?.id_verification_status, isPolling, pollSubmission, refreshProfile]);

  return {
    status,
    error,
    studentIdCard,
    isUploading,
    isPolling,
    uploadIDCard,
    refreshProfile,
    history,
    loadingHistory,
    fetchHistory,
  };
}

